(ns practitest.frontend.components2.dashboard.chart
  (:require [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components2.dashboard.card :refer [card]]
            [practitest.frontend.components2.dashboard.charts.protocol :as p]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            [practitest.frontend.components2.dashboard.charts.bar]
            [practitest.frontend.components2.dashboard.charts.readiness-gauge]
            [practitest.frontend.components2.dashboard.charts.pie]
            [practitest.frontend.components2.dashboard.charts.line]
            [practitest.frontend.components2.dashboard.charts.stacked-bar]
            [practitest.frontend.components2.tooltip :as t]
            [practitest.frontend.components2.dashboard.subs :as subs]
            [practitest.frontend.components2.dashboard.charts.render]
            [practitest.frontend.events :as events]
            [practitest.frontend.components2.dashboard.charts.vega]
            [re-frame.core :as rf]
            [reagent.core :as reagent]
            ["react" :refer [useEffect useRef]]))

(defmethod p/svg-for-export :default
  [_chart svg-element]
  (rh/clone-svg svg-element))

(defn- tooltip
  "Tooltip driven by a Reagent atom. D3 writes {:text ... :element ...} or nil."
  [state]
  (let [{:keys [text element]} @state]
    [t/tooltip
     {:show?  (some? element)
      :target element}
     (fn [_] [:span])
     text]))

(defn- menu-items [title svg-ref]
  (let [filename (or title "chart")
        items    [[:png "Download as PNG"]
                  [:jpeg "Download as JPEG"]
                  [:svg "Download as SVG"]]]
    (into [{:key  :print
            :text "Print"
            :evt  [::events/print-svg
                   {:svg-element svg-ref
                    :title       title}]}]
          (for [[format text] items]
            {:key  format
             :text text
             :evt  [::events/export-svg
                    {:format      format
                     :svg-element svg-ref
                     :filename    (str filename "." (name format))}]}))))

(defn- warning-view [{:keys [type message]} link]
  [:div.chart-error
   [:span {:class (if (= type "empty_data")
                    "empty_data"
                    "error-message")}
    message]
   (when (contains? #{"error_configuration" "no_data"} type)
     [:a {:href link}
      (str (get-translation "dashboards.chart.settings" "settings") ".")])])

(defn ref-view
  "Wrap a React ref so that @deref applies f to .-current."
  [ref f]
  (reify IDeref
    (-deref [_] (f (.-current ref)))))

(defn use-effect [f deps]
  "useEffect wrapper, compare cljs data"
  (let [ref (useRef deps)]
    (when-not (= deps (.-current ref))
      (set! (.-current ref) deps))
    (useEffect f #js [(.-current ref)])))

(defn svg
  ([item] (svg item nil))
  ([{:chart/keys [type] :as item} ext-svg-ref]
   (let [svg-ref       (or ext-svg-ref (useRef nil))
         tooltip-state (reagent/atom nil)
         config-common {:dispatch-fn #(rf/dispatch [%1 (:chart/id item) %2])
                        :tooltip     tooltip-state}]
     ;; (useEffect f #js [(hash data)]) does not work
     ;; https://clojure.atlassian.net/browse/CLJS-3410
     (use-effect
      (fn []
        (let [svg-el   (.-current svg-ref)
              observer (js/ResizeObserver. (fn []
                                             (rh/update-svg-scale! svg-el)
                                             (p/render! item config-common svg-el)))]
          (.observe observer svg-el)
          #(.disconnect observer)))
      item)
     [:<>
      [:svg.chart {:class               (some-> type name)
                   :ref                 svg-ref
                   :preserveAspectRatio "xMidYMid meet"}]
      [tooltip tooltip-state]])))

(defn- chart-inner
  [{:chart/keys [title sub-title data] :as item}
   {:chart/keys [edit-url warning]}]
  (let [svg-ref        (useRef nil)
        export-svg-ref (ref-view svg-ref #(p/svg-for-export item %))
        on-edit        #(rf/dispatch [::events/navigate edit-url])]
    [card
     {:title         title
      :sub-title     sub-title
      :on-edit       on-edit
      :dropdown-menu (menu-items title export-svg-ref)}
     (cond
       warning          [warning-view warning edit-url]
       (not (seq data)) [warning-view {:type "empty_data"
                                       :message (get-translation "dashboards.chart.no-data" "No data to display")}]
       :else            [:f> svg item svg-ref])]))

(defn chart
  "Generic chart component. Subscribes to chart data and delegates rendering."
  [{:chart/keys [id] :as props}]
  (when-let [item @(rf/subscribe [::subs/chart id])]
    [:f> chart-inner item props]))
